import { useSnackbar } from "notistack";
import React, { useState } from "react";
import {
  Box,
  Typography,
  Stack,
  InputBase,
  CircularProgress,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import {
  MdLocalFireDepartment,
  MdWarning,
  MdDirectionsRun,
  MdShield,
  MdCrisisAlert,
  MdDirectionsCar,
  MdBroadcastOnHome,
  MdNotifications,
  MdChatBubble,
  MdLock,
  MdPhone,
  MdSearch,
  MdWater,
} from "react-icons/md";
import {
  GiPistolGun,
  GiBoltBomb,
  GiMedicalPack,
  GiAmbulance,
  GiHeavyLightning,
  GiGasMask,
  GiHazardSign,
} from "react-icons/gi";
import { UpdateAlarmButton } from "../../hooks/useAlarmButtonData";
import { ALARM_BUTTON_ICONS } from "../../constants/alarmButtonIcons";

const INK = "#0a1628";
const INK_MUTED = "#5f6877";
const LINE = "#e6e4df";
const LINE_SOFT = "#eeece7";

const PRESET_COLORS = [
  { hex: "#D32F2F", label: "Röd" },
  { hex: "#F57C00", label: "Orange" },
  { hex: "#1976D2", label: "Blå" },
  { hex: "#388E3C", label: "Grön" },
  { hex: "#8a1125", label: "Varumärke" },
  { hex: "#10324a", label: "Navy" },
];

const ICON_COMPONENT = {
  "pistol": GiPistolGun,
  "bomb": GiBoltBomb,
  "fire": MdLocalFireDepartment,
  "alert": MdWarning,
  "medical-bag": GiMedicalPack,
  "ambulance": GiAmbulance,
  "run-fast": MdDirectionsRun,
  "shield": MdShield,
  "account-alert": MdCrisisAlert,
  "car": MdDirectionsCar,
  "home-alert": MdBroadcastOnHome,
  "bell": MdNotifications,
  "chat-alert": MdChatBubble,
  "lock": MdLock,
  "phone": MdPhone,
  "magnify": MdSearch,
  "weather-lightning": GiHeavyLightning,
  "water": MdWater,
  "gas-cylinder": GiGasMask,
  "hazard-lights": GiHazardSign,
};

const ICON_LABELS = {
  "pistol": "Pistol",
  "bomb": "Sprängmedel",
  "fire": "Brand",
  "alert": "Varning",
  "medical-bag": "Sjukvård",
  "ambulance": "Ambulans",
  "run-fast": "Flykt",
  "shield": "Skydd",
  "account-alert": "Person",
  "car": "Fordon",
  "home-alert": "Hemvarning",
  "bell": "Klocka",
  "chat-alert": "Meddelande",
  "lock": "Lås",
  "phone": "Telefon",
  "magnify": "Sök",
  "weather-lightning": "Blixtar",
  "water": "Vatten",
  "gas-cylinder": "Gas",
  "hazard-lights": "Fara",
};

const isPreset = (hex) => PRESET_COLORS.some((c) => c.hex === hex);

const AlarmIcon = ({ name, size = 22, color }) => {
  const Comp = ICON_COMPONENT[name];
  return Comp ? <Comp size={size} color={color} /> : null;
};

const FieldLabel = ({ children }) => (
  <Typography
    sx={{
      fontSize: "11px",
      fontWeight: 600,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: INK_MUTED,
      mb: 1,
      fontFamily: '"DM Sans", sans-serif',
    }}
  >
    {children}
  </Typography>
);

const TextInput = ({ value, onChange, placeholder }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      px: 2,
      height: 48,
      background: "#fff",
      border: `1px solid ${LINE}`,
      borderRadius: "12px",
      transition: "all 0.25s ease",
      "&:hover": { borderColor: "rgba(10,22,40,0.2)" },
      "&:focus-within": {
        borderColor: "rgba(10,22,40,0.32)",
        boxShadow: "0 0 0 4px rgba(10,22,40,0.04)",
      },
    }}
  >
    <InputBase
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      fullWidth
      sx={{
        fontSize: "15px",
        fontWeight: 500,
        fontFamily: '"DM Sans", sans-serif',
        color: INK,
        "& input::placeholder": { color: INK_MUTED, opacity: 1 },
      }}
    />
  </Box>
);

const ColorSwatch = ({ hex, label, selected, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    title={label}
    sx={{
      width: 36,
      height: 36,
      borderRadius: "8px",
      background: hex,
      border: selected ? `3px solid ${INK}` : `2px solid transparent`,
      cursor: "pointer",
      outline: "none",
      boxShadow: selected ? `0 0 0 2px #fff inset` : "none",
      transition: "all 0.2s ease",
      "&:hover": { transform: "scale(1.12)" },
    }}
  />
);

const IconGrid = ({ selectedIcon, onSelect }) => (
  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 0.75 }}>
    {ALARM_BUTTON_ICONS.map((name) => (
      <Box
        component="button"
        type="button"
        key={name}
        onClick={() => onSelect(name)}
        title={ICON_LABELS[name]}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0.5,
          py: 1.25,
          px: 0.5,
          borderRadius: "10px",
          border: `2px solid ${selectedIcon === name ? INK : LINE}`,
          background: selectedIcon === name ? "rgba(10,22,40,0.06)" : "#fff",
          cursor: "pointer",
          outline: "none",
          transition: "all 0.16s ease",
          color: selectedIcon === name ? INK : INK_MUTED,
          "&:hover": {
            borderColor: selectedIcon === name ? INK : "rgba(10,22,40,0.25)",
            background: selectedIcon === name ? "rgba(10,22,40,0.06)" : "rgba(10,22,40,0.025)",
            transform: "translateY(-1px)",
            color: INK,
          },
        }}
      >
        <AlarmIcon name={name} size={22} />
        <Typography
          sx={{
            fontSize: "11px",
            fontWeight: 500,
            color: "inherit",
            fontFamily: '"DM Sans", sans-serif',
            textAlign: "center",
            lineHeight: 1.2,
            userSelect: "none",
          }}
        >
          {ICON_LABELS[name] ?? name}
        </Typography>
      </Box>
    ))}
  </Box>
);

const ButtonPreview = ({ label, color, icon }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 2,
      p: 2,
      borderRadius: "12px",
      background: "rgba(10,22,40,0.02)",
      border: `1px dashed ${LINE}`,
      mb: 3,
    }}
  >
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: "10px",
        background: color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        transition: "background 0.2s ease",
      }}
    >
      <AlarmIcon name={icon} size={24} color="#fff" />
    </Box>
    <Box>
      <Typography
        sx={{
          fontSize: "15px",
          fontWeight: 600,
          color: label ? INK : INK_MUTED,
          fontFamily: '"DM Sans", sans-serif',
          fontStyle: label ? "normal" : "italic",
        }}
      >
        {label || "Knappetikett"}
      </Typography>
      <Typography sx={{ fontSize: "11px", color: INK_MUTED, fontFamily: '"DM Sans", sans-serif' }}>
        {icon}
      </Typography>
    </Box>
  </Box>
);

export const EditAlarmButtonModal = (props) => {
  const { button, onClose } = props;
  const { enqueueSnackbar } = useSnackbar();
  const [label, setLabel] = useState(button?.label ?? "");
  const [color, setColor] = useState(isPreset(button?.color) ? button.color : PRESET_COLORS[0].hex);
  const [customColor, setCustomColor] = useState(isPreset(button?.color) ? "" : button?.color ?? "");
  const [icon, setIcon] = useState(button?.icon ?? ALARM_BUTTON_ICONS[0]);

  const activeColor = customColor.match(/^#[0-9A-Fa-f]{6}$/)
    ? customColor
    : color;

  const updateMutation = UpdateAlarmButton({
    onSuccess: () => {
      enqueueSnackbar("Larmknapp uppdaterad.", { variant: "success" });
      onClose();
    },
    onError: () => {
      enqueueSnackbar("Kunde inte uppdatera larmknapp.", { variant: "error" });
    },
  });

  const handleSave = () => {
    updateMutation.mutate({
      id: button.id,
      data: { label: label.trim(), color: activeColor, icon },
    });
  };

  const saving = updateMutation.isLoading;
  const canSave = label.trim().length > 0 && !saving;

  return (
    <Box>
      <ButtonPreview label={label} color={activeColor} icon={icon} />

      <Stack spacing={3} sx={{ mb: 4 }}>
        <Box>
          <FieldLabel>Knappetikett</FieldLabel>
          <TextInput
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="T.ex. Brandlarm"
          />
        </Box>

        <Box>
          <FieldLabel>Färg</FieldLabel>
          <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
            {PRESET_COLORS.map((c) => (
              <ColorSwatch
                key={c.hex}
                hex={c.hex}
                label={c.label}
                selected={activeColor === c.hex && !customColor.match(/^#[0-9A-Fa-f]{6}$/)}
                onClick={() => {
                  setColor(c.hex);
                  setCustomColor("");
                }}
              />
            ))}
          </Stack>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 2,
              height: 40,
              background: "#fff",
              border: `1px solid ${LINE}`,
              borderRadius: "10px",
            }}
          >
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: "4px",
                background: activeColor,
                border: `1px solid ${LINE}`,
                flexShrink: 0,
              }}
            />
            <InputBase
              value={customColor}
              onChange={(e) => setCustomColor(e.target.value)}
              placeholder="#RRGGBB"
              sx={{
                fontSize: "13px",
                fontFamily: '"DM Sans", sans-serif',
                color: INK,
                "& input::placeholder": { color: INK_MUTED, opacity: 1 },
              }}
            />
          </Box>
        </Box>

        <Box>
          <FieldLabel>Ikon</FieldLabel>
          <IconGrid selectedIcon={icon} onSelect={setIcon} />
        </Box>
      </Stack>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1.25,
          pt: 3,
          borderTop: `1px solid ${LINE_SOFT}`,
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={() => onClose()}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            px: 2.25,
            py: 1.25,
            background: "transparent",
            color: INK_MUTED,
            border: `1px solid ${LINE}`,
            borderRadius: "10px",
            fontFamily: '"DM Sans", sans-serif',
            fontSize: "13px",
            fontWeight: 600,
            cursor: "pointer",
            transition: "all 0.25s ease",
            "&:hover": { color: INK, borderColor: "rgba(10,22,40,0.22)" },
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: 16 }} />
          Avbryt
        </Box>
        <Box
          component="button"
          type="button"
          onClick={handleSave}
          disabled={!canSave}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            px: 2.75,
            py: 1.25,
            background: INK,
            color: "#fff",
            border: "none",
            borderRadius: "10px",
            fontFamily: '"DM Sans", sans-serif',
            fontSize: "13px",
            fontWeight: 600,
            cursor: canSave ? "pointer" : "not-allowed",
            opacity: canSave ? 1 : 0.4,
            transition: "all 0.25s ease",
            boxShadow: canSave ? "0 10px 24px -12px rgba(10,22,40,0.55)" : "none",
            "&:hover": canSave
              ? { background: "#1a2638", transform: "translateY(-1px)" }
              : {},
          }}
        >
          {saving ? (
            <CircularProgress size={14} sx={{ color: "#fff" }} />
          ) : (
            <CheckRoundedIcon sx={{ fontSize: 16 }} />
          )}
          Spara
        </Box>
      </Box>
    </Box>
  );
};
